import { DROPBOX_KEY, MS_CLIENT_ID } from './flavor.js';
import { storageAvailable } from './storage-bridge.js';

/* Conectar una cuenta de nube (Dropbox, OneDrive) sin contraseñas ni servidor de por medio: se entra en la página del
   propio servicio y a la app solo le llega un permiso de LECTURA que se puede retirar desde la cuenta.
     · Dropbox: código con PKCE sin dirección de retorno: el servicio enseña un código que se pega en la app.
     · OneDrive: «código de dispositivo» (como entrar en una tele): se escribe un código en microsoft.com/devicelogin.
   Las claves de las apps (públicas) se ponen al compilar: deploy/cloud-keys.json o FILOTECA_DROPBOX_KEY / FILOTECA_MS_CLIENT_ID.
   Todo recibe `fetch` para poder probarse sin red. */

export const CLOUDS = {
    dropbox: { icon: 'cloud', key: () => DROPBOX_KEY },
    onedrive: { icon: 'cloud', key: () => MS_CLIENT_ID }
};

/** ¿Se puede conectar esta nube en este aparato? Hace falta la clave de la app y el servidor local nativo que la lee. */
export function cloudAvailable(kind, { key = null, native = storageAvailable } = {}) {
    if (!CLOUDS[kind]) return false;
    const k = key === null ? CLOUDS[kind].key() : key;
    return !!k && native(kind);
}

const form = (obj) => Object.keys(obj).map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(obj[k])).join('&');

async function post(fetchFn, url, body, headers = {}) {
    let r;
    try {
        r = await fetchFn(url, { method: 'POST', headers: Object.assign({ 'Content-Type': 'application/x-www-form-urlencoded' }, headers), body: typeof body === 'string' ? body : form(body) });
    } catch (e) {
        // Sin red, o el navegador no deja llegar al servicio (CORS): se cuenta tal cual para poder verlo.
        const err = fail('network', 0);
        err.detail = 'fetch: ' + String((e && e.message) || e).slice(0, 120);
        throw err;
    }
    let json = {};
    try { json = await r.json(); } catch (e) { /* sin cuerpo */ }
    return { status: r.status, json };
}

function fail(code, status) {
    return Object.assign(new Error(code), { code, status });
}

/** Cadena aleatoria apta para PKCE (43 caracteres, solo los permitidos). */
export function randomVerifier(random = (n) => { const a = new Uint8Array(n); crypto.getRandomValues(a); return a; }) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    return Array.from(random(43)).map((b) => chars[b % chars.length]).join('');
}

const b64url = (buf) => btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(buf)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

/** S256 si el contexto lo permite; si no (la app se abre desde file://, sin crypto.subtle), «plain», que Dropbox también acepta. */
export async function challengeFor(verifier, subtle = typeof crypto !== 'undefined' ? crypto.subtle : null) {
    if (subtle && subtle.digest) {
        const digest = await subtle.digest('SHA-256', new TextEncoder().encode(verifier));
        return { challenge: b64url(digest), method: 'S256' };
    }
    return { challenge: verifier, method: 'plain' };
}

/* ---------------------------------------------------------------- Dropbox */

export const DROPBOX = { authorize: 'https://www.dropbox.com/oauth2/authorize', token: 'https://api.dropboxapi.com/oauth2/token', api: 'https://api.dropboxapi.com/2' };

/** { url, verifier }: la página de Dropbox a abrir y lo que hay que recordar hasta que se pegue el código. */
export async function dropboxStart(key, { verifier = randomVerifier(), subtle, redirectUri = '', state = '' } = {}) {
    if (!key) throw fail('unconfigured');
    const { challenge, method } = await challengeFor(verifier, subtle);
    // Con dirección de retorno (http://localhost:puerto/oauth/dropbox) Dropbox vuelve solo a la app; sin ella, enseña un código que se pega.
    const params = { client_id: key, response_type: 'code', token_access_type: 'offline', code_challenge: challenge, code_challenge_method: method };
    if (redirectUri) { params.redirect_uri = redirectUri; params.state = state; }
    const url = DROPBOX.authorize + '?' + form(params);
    return { url, verifier };
}

/** Código pegado → { refreshToken, accessToken, expiresIn }. */
export async function dropboxExchange(key, verifier, code, fetchFn = fetch, redirectUri = '') {
    const clean = String(code || '').trim();
    if (!clean) throw fail('code');
    const body = { code: clean, grant_type: 'authorization_code', code_verifier: verifier, client_id: key };
    if (redirectUri) body.redirect_uri = redirectUri; // la misma con la que se pidió el permiso
    const r = await post(fetchFn, DROPBOX.token, body);
    if (r.status !== 200 || !r.json.access_token) throw fail(r.json.error === 'invalid_grant' ? 'code' : 'network', r.status);
    if (!r.json.refresh_token) throw fail('offline');
    return { refreshToken: r.json.refresh_token, accessToken: r.json.access_token, expiresIn: Number(r.json.expires_in) || 14400 };
}

/** Datos de la cuenta para ponerle nombre a la fuente. */
export async function dropboxAccount(accessToken, fetchFn = fetch) {
    const r = await fetchFn(DROPBOX.api + '/users/get_current_account', { method: 'POST', headers: { Authorization: 'Bearer ' + accessToken } });
    if (!r.ok) throw fail('network', r.status);
    const j = await r.json();
    return { email: String(j.email || ''), name: String((j.name && j.name.display_name) || '') };
}

/* ---------------------------------------------------------------- OneDrive */

export const MICROSOFT = { devicecode: 'https://login.microsoftonline.com/common/oauth2/v2.0/devicecode', token: 'https://login.microsoftonline.com/common/oauth2/v2.0/token', graph: 'https://graph.microsoft.com/v1.0', scope: 'Files.Read offline_access User.Read' };

export const MICROSOFT_AUTH = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';

/** Con navegador en este mismo aparato: el permiso se pide en la página de Microsoft y vuelve solo a la app. */
export async function msAuthStart(clientId, { verifier = randomVerifier(), subtle, redirectUri, state } = {}) {
    if (!clientId) throw fail('unconfigured');
    const { challenge, method } = await challengeFor(verifier, subtle);
    const url = MICROSOFT_AUTH + '?' + form({ client_id: clientId, response_type: 'code', redirect_uri: redirectUri, response_mode: 'query', scope: MICROSOFT.scope, state, code_challenge: challenge, code_challenge_method: method, prompt: 'select_account' });
    return { url, verifier };
}

/** Código recibido → tokens (cliente público: sin secreto, con el verificador). */
export async function msAuthExchange(clientId, verifier, code, redirectUri, fetchFn = fetch) {
    const r = await post(fetchFn, MICROSOFT.token, { client_id: clientId, grant_type: 'authorization_code', code: String(code || '').trim(), redirect_uri: redirectUri, code_verifier: verifier, scope: MICROSOFT.scope });
    if (r.status !== 200 || !r.json.access_token) {
        const err = fail(r.json.error === 'invalid_grant' ? 'code' : 'network', r.status);
        err.detail = [r.json.error, r.json.error_description ? String(r.json.error_description).split('\r')[0].split('\n')[0].slice(0, 160) : ''].filter(Boolean).join(': ') || ('HTTP ' + r.status);
        throw err;
    }
    if (!r.json.refresh_token) throw fail('offline');
    return { refreshToken: r.json.refresh_token, accessToken: r.json.access_token, expiresIn: Number(r.json.expires_in) || 3600 };
}

/* ---------------------------------------------------------------- vuelta del navegador (dirección local) */

/** El puerto fijo en el que el servidor local escucha (src/core/storage-bridge.js): las nubes tienen registrada su dirección. */
export const LOOPBACK_PORT = 18790;

/** Dirección de retorno registrada en la app de cada servicio. «localhost» y no 127.0.0.1, que es lo que admiten. */
export const loopbackUri = (kind, port = LOOPBACK_PORT) => 'http://localhost:' + port + '/oauth/' + kind;

/** ¿Se puede volver solo a la app? Hace falta el puerto de siempre y un navegador en este aparato (una tele no lo tiene). */
export function loopbackAvailable({ port, isTV = false } = {}) {
    return Number(port) === LOOPBACK_PORT && !isTV;
}

export function randomState(random = (n) => { const a = new Uint8Array(n); crypto.getRandomValues(a); return a; }) {
    return Array.from(random(24)).map((b) => (b % 36).toString(36)).join('');
}

/** Espera a que el navegador vuelva a la dirección local: devuelve el código; lanza si se rechaza, caduca o se cancela. */
export async function waitForCallback(port, state, { fetchFn = fetch, sleep = (ms) => new Promise((r) => setTimeout(r, ms)), now = Date.now, timeoutMs = 10 * 60 * 1000, cancel = { stop: false }, onTick = () => {} } = {}) {
    const until = now() + timeoutMs;
    while (now() < until) {
        if (cancel.stop) throw fail('cancelled');
        await sleep(1000);
        if (cancel.stop) throw fail('cancelled');
        onTick(Math.max(0, Math.round((until - now()) / 1000)));
        let j = null;
        try {
            const r = await fetchFn('http://127.0.0.1:' + port + '/oauth/result?state=' + encodeURIComponent(state));
            j = r.ok ? await r.json() : null;
        } catch (e) { j = null; } // el servidor local aún no contesta: se sigue esperando
        if (!j || j.pending) continue;
        if (j.code) return j.code;
        const err = fail(j.error === 'access_denied' ? 'declined' : 'network');
        err.detail = [j.error, j.description].filter(Boolean).join(': ').slice(0, 200);
        throw err;
    }
    throw fail('expired');
}

/** Empieza el «código de dispositivo» → { deviceCode, userCode, url, interval, expiresIn }. */
export async function msDeviceStart(clientId, fetchFn = fetch) {
    if (!clientId) throw fail('unconfigured');
    const r = await post(fetchFn, MICROSOFT.devicecode, { client_id: clientId, scope: MICROSOFT.scope });
    if (r.status !== 200 || !r.json.device_code) throw fail('network', r.status);
    return { deviceCode: r.json.device_code, userCode: r.json.user_code, url: r.json.verification_uri || 'https://microsoft.com/devicelogin', interval: Math.max(2, Number(r.json.interval) || 5), expiresIn: Number(r.json.expires_in) || 900 };
}

/** Una consulta: { pending: true } mientras no se haya aprobado; los tokens al aprobarse; lanza si se rechaza o caduca. */
export async function msDevicePoll(clientId, deviceCode, fetchFn = fetch) {
    const r = await post(fetchFn, MICROSOFT.token, { grant_type: 'urn:ietf:params:oauth:grant-type:device_code', client_id: clientId, device_code: deviceCode });
    if (r.status === 200 && r.json.access_token) {
        if (!r.json.refresh_token) throw fail('offline');
        return { refreshToken: r.json.refresh_token, accessToken: r.json.access_token, expiresIn: Number(r.json.expires_in) || 3600 };
    }
    const e = r.json.error;
    if (e === 'authorization_pending' || e === 'slow_down') return { pending: true, slow: e === 'slow_down' };
    if (e === 'expired_token' || e === 'code_expired') throw fail('expired');
    if (e === 'authorization_declined' || e === 'access_denied') throw fail('declined');
    const err = fail('network', r.status);
    err.detail = [e, r.json.error_description ? String(r.json.error_description).split('\r')[0].split('\n')[0].slice(0, 160) : ''].filter(Boolean).join(': ') || ('HTTP ' + r.status);
    throw err;
}

/** Espera a que se apruebe en el otro aparato. onTick(segundosRestantes) para pintar la cuenta atrás; cancel.stop = true la corta. */
export async function msDeviceWait(clientId, start, { fetchFn = fetch, sleep = (ms) => new Promise((r) => setTimeout(r, ms)), onTick = () => {}, onStatus = () => {}, cancel = { stop: false }, now = Date.now } = {}) {
    const until = now() + start.expiresIn * 1000;
    let interval = start.interval;
    while (now() < until) {
        if (cancel.stop) throw fail('cancelled');
        await sleep(interval * 1000);
        if (cancel.stop) throw fail('cancelled');
        onTick(Math.max(0, Math.round((until - now()) / 1000)));
        let r;
        try {
            r = await msDevicePoll(clientId, start.deviceCode, fetchFn);
        } catch (e) {
            onStatus('error', e);
            throw e;
        }
        onStatus(r.pending ? 'pending' : 'approved');
        if (!r.pending) return r;
        if (r.slow) interval += 5;
    }
    throw fail('expired');
}

export async function msAccount(accessToken, fetchFn = fetch) {
    const r = await fetchFn(MICROSOFT.graph + '/me', { headers: { Authorization: 'Bearer ' + accessToken } });
    if (!r.ok) throw fail('network', r.status);
    const j = await r.json();
    return { email: String(j.mail || j.userPrincipalName || ''), name: String(j.displayName || '') };
}
